/**
 * The inline marks of a long-text field the site renders with them (F-R16-04): fe-svet's
 * `lib/inline-text.ts` turns `**bold**`, `_italic_` and `==highlight==` into strong, em
 * and mark, beside the `[text](url)` links of F-R14-01. The field stays a plain
 * `<textarea>` holding plain text; the toolbar only writes and removes the markers.
 *
 * The rules mirror the site's parser, so what the toolbar writes is what the site
 * renders:
 * - a marker sits directly against its words, so the selection's outer spaces are left
 *   outside it (`** word **` would render literally);
 * - an italic `_` has to sit at a word's edge, so the italic selection grows to whole
 *   words (`_snake_` inside `snake_case` would render literally);
 * - marks never cross a blank line, so a selection over several paragraphs marks each
 *   paragraph on its own.
 */

export type MarkKind = 'bold' | 'italic' | 'highlight';

export const MARK_KINDS: readonly MarkKind[] = ['bold', 'italic', 'highlight'];

export const MARKERS: Readonly<Record<MarkKind, string>> = {
  bold: '**',
  italic: '_',
  highlight: '==',
};

/** One edit: replace `[from, to)` with `insert`, then select `[selStart, selEnd)`. */
export interface MarkEdit {
  from: number;
  to: number;
  insert: string;
  selStart: number;
  selEnd: number;
}

const WORD = /[\p{L}\p{N}]/u;
const BLANK_LINE = /\n[ \t]*\n/;

/**
 * Where `kind`'s markers sit around or just inside the selection `[start, end)`, looking
 * through any other marks between them: with `word` selected in `**_word_**`, bold's
 * markers are found outside the italic ones. Null when the selection is not marked.
 */
function markedAt(value: string, start: number, end: number, kind: MarkKind): { open: number; close: number } | null {
  if (start === end) return null;
  const m = MARKERS[kind];
  // Outward: peel matching marker pairs off the text around the selection.
  for (let l = start, r = end; ; ) {
    const layer = MARK_KINDS.map((k) => MARKERS[k]).find(
      (mm) => value.slice(l - mm.length, l) === mm && value.slice(r, r + mm.length) === mm,
    );
    if (!layer) break;
    if (layer === m) return { open: l - m.length, close: r };
    l -= layer.length;
    r += layer.length;
  }
  // Inward: peel matching marker pairs off the selection's own ends.
  for (let l = start, r = end; r - l > 0; ) {
    const layer = MARK_KINDS.map((k) => MARKERS[k]).find(
      (mm) => r - l > 2 * mm.length && value.startsWith(mm, l) && value.slice(r - mm.length, r) === mm,
    );
    if (!layer) break;
    if (layer === m) return { open: l, close: r - m.length };
    l += layer.length;
    r -= layer.length;
  }
  return null;
}

/** Whether the selection `[start, end)` is marked with `kind` (the button's pressed state). */
export function isMarked(value: string, start: number, end: number, kind: MarkKind): boolean {
  return markedAt(value, start, end, kind) !== null;
}

/**
 * The edit that toggles `kind` on the selection `[start, end)` of `value`: removes the
 * markers when the selection is already marked (around it or just inside it, through
 * any other marks), and otherwise wraps it. An empty selection inserts an empty pair
 * with the caret between, ready to type into. Null when there is nothing to do (a
 * selection of spaces only).
 */
export function toggleMark(value: string, start: number, end: number, kind: MarkKind): MarkEdit | null {
  const m = MARKERS[kind];
  if (start === end) {
    return { from: start, to: end, insert: m + m, selStart: start + m.length, selEnd: start + m.length };
  }
  const marked = markedAt(value, start, end, kind);
  if (marked) {
    const { open, close } = marked;
    const insert = value.slice(open + m.length, close);
    // The selection keeps its words: each end moves left by the markers before it.
    const shift = (at: number) => at - (at > open ? m.length : 0) - (at > close ? m.length : 0);
    return { from: open, to: close + m.length, insert, selStart: shift(start), selEnd: shift(end) };
  }

  let from = start;
  let to = end;
  if (kind === 'italic') {
    while (from > 0 && WORD.test(value.charAt(from - 1))) from--;
    while (to < value.length && WORD.test(value.charAt(to))) to++;
  }
  const text = value.slice(from, to);
  if (text.trim() === '') return null;
  const insert = text
    .split(new RegExp(`(${BLANK_LINE.source})`))
    .map((part) => (BLANK_LINE.test(part) ? part : wrap(part, m)))
    .join('');
  const lead = insert.length - insert.trimStart().length;
  const trail = insert.length - insert.trimEnd().length;
  const single = !BLANK_LINE.test(text);
  return {
    from,
    to,
    insert,
    // One paragraph: select the words inside the new markers, so a second click (or the
    // same shortcut) takes them off again. Several: select the whole marked text.
    selStart: from + lead + (single ? m.length : 0),
    selEnd: from + insert.length - trail - (single ? m.length : 0),
  };
}

/** `part` with `m` around its words and its outer whitespace left outside. */
function wrap(part: string, m: string): string {
  const words = part.trim();
  if (words === '') return part;
  const lead = part.slice(0, part.length - part.trimStart().length);
  const trail = part.slice(part.trimEnd().length);
  return `${lead}${m}${words}${m}${trail}`;
}
